import React, { useEffect, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { PhoneOff, Phone, LogOut, Video, ShieldCheck, Shield, AlertCircle } from 'lucide-react';
import './styles/RecordingModal.css';

const RecordingModal = ({
  isOpen,
  isModalFor,
  endCallModalIntent,
  clearEndCallModalIntent,
  clientObjectArr,
  userId,
  displayName,
  acsRoomId,
  senderUserIdGlobal,
  senderSocketIdGlobal,
  socket,
  setIsRecordingModal,
  setIsAlreadyJoined,
  clickOnDisconnectIcon,
  disconnectCall,
  startCallOnClick,
  stopRecording,
  setRecordingStatus,
  setRecordingInitiatorId,
  setIsRecordingConsentRequested,
  modalRefParticipant,
  recordingToast
}) => {
  const checkMultipleAdmins = clientObjectArr?.filter(item => item?.userType === 'Admin') || [];
  // Check if there's any co-host assigned (someone with cohost === true)
  const hasCoHost = clientObjectArr?.some(item => item?.cohost === true) || false;
  // Show Leave button when host has no co-host assigned
  const showLeaveButton = !hasCoHost && isModalFor === 'EndCall';
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showLeaveConfirmationModal, setShowLeaveConfirmationModal] = useState(false);

  // Handle Yes click in Leave confirmation modal
  const handleLeaveYesClick = useCallback(() => {
    // Close all modals so host can assign a co-host
    setShowLeaveConfirmationModal(false);
    setIsRecordingModal(false);
  }, []);


  // Handle No click in Leave confirmation modal
  const handleLeaveNoClick = useCallback(() => {
    // End the meeting
    setShowLeaveConfirmationModal(false);
    clickOnDisconnectIcon('EndCallByAdmin');
    setIsRecordingModal(false);
  }, [clickOnDisconnectIcon, setIsRecordingModal]);

  // Handle Yes click
  const handleYesClick = useCallback(async () => {
    if (isModalFor === 'Recording') {
      const tempObj = { userId, userType: 'Admin', socketId: socket.id, displayName, recordingStatus: 'start' };
      socket.emit('BE-update-status', { acsRoomId, senderUserId: senderUserIdGlobal, socketId: senderSocketIdGlobal, data: tempObj, type: 'acceptRecording', typeValue: true });
    } else if (isModalFor === 'EndCall') {
      console.log('Click on End Call inside the Yes or No Modal');
      clickOnDisconnectIcon('EndCallByAdmin');
    } else if (isModalFor === 'StopRecording') {
      // Stop the recording
      let response = await stopRecording();
      if (response.recordingId) {
        socket.emit('BE-update-status', { acsRoomId: acsRoomId, senderUserId: userId, socketId: socket.id, data: { isSuccessMsg: false, status: 'RecordingStopped' }, type: 'BroadCastMessage', typeValue: 'Recording has stopped' });
        setRecordingStatus('initial');
        setRecordingInitiatorId(null);
        setIsRecordingConsentRequested(false);
        if (modalRefParticipant?.current) {
          modalRefParticipant.current.updateRecordingFlag('initial');
        }
        recordingToast.success('Your recording has been successfully saved.');
      }
    } else {
      setIsAlreadyJoined(false);
      socket.emit('BE-update-status', { acsRoomId, senderUserId: senderUserIdGlobal, socketId: senderSocketIdGlobal, data: null, type: 'RemoveExistingAddSameUser', typeValue: true });
      startCallOnClick();
    }
    setIsRecordingModal(false);
  }, [isModalFor, userId, socket, acsRoomId, senderUserIdGlobal, senderSocketIdGlobal, displayName, clickOnDisconnectIcon, setIsAlreadyJoined, startCallOnClick, setIsRecordingModal, stopRecording, setRecordingStatus, setRecordingInitiatorId, setIsRecordingConsentRequested, modalRefParticipant, recordingToast]);

  // Handle No click
  const handleNoClick = useCallback(() => {
    if (isModalFor === 'Recording') {
      // Show confirmation modal instead of directly denying
      setShowConfirmationModal(true);
    } else if (isModalFor === 'StopRecording') {
      // Close modal and continue recording
      setIsRecordingModal(false);
    } else {
      setIsRecordingModal(false);
      if (isModalFor === 'AlreadyExist') {
        setIsAlreadyJoined(true);
      }
    }
  }, [isModalFor, setIsAlreadyJoined, setIsRecordingModal]);

  // Handle "Change to Agree" in confirmation modal
  const handleChangeToAgree = useCallback(() => {
    setShowConfirmationModal(false);
    // Call the same flow as clicking "Agree"
    const tempObj = { userId, userType: 'Admin', socketId: socket.id, displayName, recordingStatus: 'start' };
    socket.emit('BE-update-status', { acsRoomId, senderUserId: senderUserIdGlobal, socketId: senderSocketIdGlobal, data: tempObj, type: 'acceptRecording', typeValue: true });
    setIsRecordingModal(false);
  }, [userId, socket, acsRoomId, senderUserIdGlobal, senderSocketIdGlobal, displayName, setIsRecordingModal]);

  // Handle "Dismiss" in confirmation modal
  const handleDismiss = useCallback(() => {
    setShowConfirmationModal(false);
    const tempObj = { userId, userType: 'Admin', socketId: socket.id, displayName, recordingStatus: 'start' };
    socket.emit('BE-update-status', { acsRoomId, senderUserId: senderUserIdGlobal, socketId: senderSocketIdGlobal, data: tempObj, type: 'rejectRecording', typeValue: true });
    toast.error('Your rejection request has been sent to all Participants.');
    setIsRecordingModal(false);
  }, [userId, socket, acsRoomId, senderUserIdGlobal, senderSocketIdGlobal, displayName, setIsRecordingModal]);

  // Keyboard shortcut handlers for EndCall, Recording, and StopRecording modals
  useEffect(() => {
    if (!isOpen || (isModalFor !== 'EndCall' && isModalFor !== 'Recording' && isModalFor !== 'StopRecording')) return;

    const handleKeyPress = (e) => {
      if (showLeaveConfirmationModal) {
        // Keyboard shortcuts for leave confirmation modal
        if (e.key === 'Enter') {
          e.preventDefault();
          handleLeaveYesClick();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          handleLeaveNoClick();
        }
      } else if (showConfirmationModal) {
        // Keyboard shortcuts for recording confirmation modal
        if (e.key === 'Enter') {
          e.preventDefault();
          handleChangeToAgree();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          handleDismiss();
        }
      } else {
        // Keyboard shortcuts for main modal
        if (e.key === 'Enter') {
          e.preventDefault();
          handleYesClick();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          handleNoClick();
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, isModalFor, showConfirmationModal, showLeaveConfirmationModal, handleYesClick, handleNoClick, handleChangeToAgree, handleDismiss, handleLeaveYesClick, handleLeaveNoClick]);

  const handleLeaveClick = useCallback(() => {
    if (isModalFor === 'EndCall') {
      // Show confirmation modal instead of directly leaving
      setShowLeaveConfirmationModal(true);
    }
  }, [isModalFor]);

  useEffect(() => {
    if (isOpen && isModalFor === 'EndCall' && endCallModalIntent === 'leave') {
      setShowLeaveConfirmationModal(true);
      if (typeof clearEndCallModalIntent === 'function') {
        clearEndCallModalIntent();
      }
    }
  }, [isOpen, isModalFor, endCallModalIntent, clearEndCallModalIntent]);


  if (!isOpen) return null;

  // For StopRecording modal, use the new design
  if (isModalFor === 'StopRecording') {
    return (
      <>
        {/* Animation styles */}
        <style>
          {`
            @keyframes modalFadeIn {
              from {
                opacity: 0;
                transform: scale(0.95);
              }
              to {
                opacity: 1;
                transform: scale(1);
              }
            }
            
            /* Responsive styles */
            @media (max-width: 480px) {
              .end-call-modal-content {
                padding: 24px !important;
              }
              .end-call-modal-title {
                font-size: 24px !important;
              }
              .end-call-modal-subtitle {
                font-size: 16px !important;
              }
            }
          `}
        </style>

        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          backdropFilter: 'blur(4px)'
        }}>
          <div
            className="end-call-modal-content"
            style={{
              backgroundColor: '#1C1F2E',
              borderRadius: '16px',
              padding: '40px',
              maxWidth: '90%',
              width: '400px',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
              position: 'relative',
              animation: 'modalFadeIn 0.3s ease-out'
            }}>
            {/* Icon container */}
            <div style={{
              width: '64px',
              height: '64px',
              margin: '0 auto 24px',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <AlertCircle size={32} style={{ color: '#EF4444' }} />
            </div>

            {/* Title */}
            <h2
              className="end-call-modal-title"
              style={{
                color: '#FFFFFF',
                fontSize: '28px',
                fontWeight: '600',
                textAlign: 'center',
                margin: '0 0 8px 0',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
              }}>
              Stop Recording
            </h2>

            {/* Message */}
            <p
              className="end-call-modal-subtitle"
              style={{
                color: '#9CA3AF',
                fontSize: '18px',
                textAlign: 'center',
                margin: '0 0 32px 0',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
              }}>
              Are you sure you want to stop the recording?
            </p>

            {/* Buttons container */}
            <div style={{
              display: 'flex',
              gap: '12px',
              marginBottom: '24px',
              flexWrap: 'wrap'
            }}>
              {/* Yes button */}
              <button
                onClick={handleYesClick}
                style={{
                  flex: 1,
                  minWidth: '120px',
                  padding: '12px 24px',
                  backgroundColor: '#EA580C',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#DC2626';
                  e.target.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#EA580C';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                <PhoneOff size={18} />
                Yes
              </button>

              {/* No button */}
              <button
                onClick={handleNoClick}
                style={{
                  flex: 1,
                  minWidth: '120px',
                  padding: '12px 24px',
                  backgroundColor: '#374151',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#4B5563';
                  e.target.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#374151';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                <Phone size={18} />
                No
              </button>
            </div>

            {/* Keyboard shortcuts hint */}
            <p style={{
              color: '#6B7280',
              fontSize: '12px',
              textAlign: 'center',
              margin: '0',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            }}>
              Press Enter to confirm • Escape to cancel
            </p>
          </div>
        </div>
      </>
    );
  }

  // For EndCall modal, use the new design
  if (isModalFor === 'EndCall') {
    // Show leave confirmation modal if it's open
    if (showLeaveConfirmationModal) {
      return (
        <>
          {/* Animation styles */}
          <style>
            {`
              @keyframes modalFadeIn {
                from {
                  opacity: 0;
                  transform: scale(0.95);
                }
                to {
                  opacity: 1;
                  transform: scale(1);
                }
              }
              
              /* Responsive styles */
              @media (max-width: 480px) {
                .end-call-modal-content {
                  padding: 24px !important;
                }
                .end-call-modal-title {
                  font-size: 24px !important;
                }
                .end-call-modal-subtitle {
                  font-size: 16px !important;
                }
              }
            `}
          </style>

          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001,
            backdropFilter: 'blur(4px)'
          }}>
            <div
              className="end-call-modal-content"
              style={{
                backgroundColor: '#1C1F2E',
                borderRadius: '16px',
                padding: '40px',
                maxWidth: '90%',
                width: '400px',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
                position: 'relative',
                animation: 'modalFadeIn 0.3s ease-out'
              }}>
              {/* Icon container */}
              <div style={{
                width: '64px',
                height: '64px',
                margin: '0 auto 24px',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <AlertCircle size={32} style={{ color: '#EF4444' }} />
              </div>

              {/* Title */}
              <h2
                className="end-call-modal-title"
                style={{
                  color: '#FFFFFF',
                  fontSize: '28px',
                  fontWeight: '600',
                  textAlign: 'center',
                  margin: '0 0 8px 0',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}>
                No Co-Host Assigned
              </h2>

              {/* Message */}
              <p
                className="end-call-modal-subtitle"
                style={{
                  color: '#9CA3AF',
                  fontSize: '16px',
                  textAlign: 'center',
                  margin: '0 0 32px 0',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  lineHeight: '1.5'
                }}>
                You haven't assigned anyone as a co-host. If you end the meeting, it will end without a host. To continue the meeting, please assign someone as a co-host.
              </p>

              {/* Buttons container */}
              <div style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '24px',
                flexWrap: 'wrap'
              }}>
                {/* Yes button */}
                <button
                  onClick={handleLeaveYesClick}
                  style={{
                    flex: 1,
                    minWidth: '120px',
                    padding: '12px 24px',
                    backgroundColor: '#10B981',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#059669';
                    e.target.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#10B981';
                    e.target.style.transform = 'scale(1)';
                  }}
                >
                  <ShieldCheck size={18} />
                  Yes
                </button>

                {/* No button */}
                <button
                  onClick={handleLeaveNoClick}
                  style={{
                    flex: 1,
                    minWidth: '120px',
                    padding: '12px 24px',
                    backgroundColor: '#EA580C',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#DC2626';
                    e.target.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#EA580C';
                    e.target.style.transform = 'scale(1)';
                  }}
                >
                  <PhoneOff size={18} />
                  No
                </button>
              </div>

              {/* Keyboard shortcuts hint */}
              <p style={{
                color: '#6B7280',
                fontSize: '12px',
                textAlign: 'center',
                margin: '0',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
              }}>
                Press Enter to assign co-host • Escape to end meeting
              </p>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        {/* Animation styles */}
        <style>
          {`
            @keyframes modalFadeIn {
              from {
                opacity: 0;
                transform: scale(0.95);
              }
              to {
                opacity: 1;
                transform: scale(1);
              }
            }
            
            /* Responsive styles */
            @media (max-width: 480px) {
              .end-call-modal-content {
                padding: 24px !important;
              }
              .end-call-modal-title {
                font-size: 24px !important;
              }
              .end-call-modal-subtitle {
                font-size: 16px !important;
              }
            }
          `}
        </style>

        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          backdropFilter: 'blur(4px)'
        }}>
          <div
            className="end-call-modal-content"
            style={{
              backgroundColor: '#1C1F2E',
              borderRadius: '16px',
              padding: '40px',
              maxWidth: '90%',
              width: '400px',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
              position: 'relative',
              animation: 'modalFadeIn 0.3s ease-out'
            }}>
            {/* Icon container */}
            <div style={{
              width: '64px',
              height: '64px',
              margin: '0 auto 24px',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <PhoneOff size={32} style={{ color: '#EF4444' }} />
            </div>

            {/* Title */}
            <h2
              className="end-call-modal-title"
              style={{
                color: '#FFFFFF',
                fontSize: '22px',
                fontWeight: '600',
                textAlign: 'center',
                margin: '0 0 8px 0',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
              }}>
              End the meeting?
            </h2>

            {/* Subtitle */}
            {/* <p
              className="end-call-modal-subtitle"
              style={{
                color: '#9CA3AF',
                fontSize: '18px',
                textAlign: 'center',
                margin: '0 0 24px 0',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
              }}>
              Meeting will end
            </p> */}

            {/* Question text */}
            <p style={{
              color: '#E5E7EB',
              fontSize: '16px',
              textAlign: 'center',
              margin: '0 0 32px 0',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            }}>
              You'll end the meeting for everyone.
            </p>

            {/* Buttons container */}
            <div style={{
              display: 'flex',
              gap: '12px',
              marginBottom: showLeaveButton ? '12px' : '24px',
              flexWrap: 'wrap'
            }}>
              {/* Yes button */}
              <button
                onClick={handleYesClick}
                style={{
                  flex: 1,
                  minWidth: '120px',
                  padding: '12px 24px',
                  backgroundColor: '#EA580C',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#DC2626';
                  e.target.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#EA580C';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                <PhoneOff size={18} />
                End
              </button>

              {/* No button */}
              <button
                onClick={handleNoClick}
                style={{
                  flex: 1,
                  minWidth: '120px',
                  padding: '12px 24px',
                  backgroundColor: '#374151',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#4B5563';
                  e.target.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#374151';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                <Phone size={18} />
                Cancel
              </button>
            </div>

            {/* Leave button - only show when no co-host is assigned */}
            {/* {showLeaveButton && (
              <button
                onClick={handleLeaveClick}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  backgroundColor: '#DC2626',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  marginBottom: '24px',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#B91C1C';
                  e.target.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#DC2626';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                <LogOut size={18} />
                Leave
              </button>
            )} */}

            {/* Keyboard shortcuts hint */}
            <p style={{
              color: '#6B7280',
              fontSize: '12px',
              textAlign: 'center',
              margin: '0',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            }}>
              Press Enter to confirm • Escape to cancel
            </p>
          </div>
        </div>
      </>
    );
  }

  // For Recording modal, use the new design
  if (isModalFor === 'Recording') {
    // Show confirmation modal if user clicked "Deny"
    if (showConfirmationModal) {
      return (
        <div className="recording-consent-overlay">
          <div className="recording-consent-modal">
            {/* Icon at top - Red alert icon */}
            <div className="recording-denial-icon-circle">
              <AlertCircle className="recording-denial-icon" size={40} />
            </div>

            {/* Heading */}
            <h2 className="recording-consent-heading">
              Recording denied
            </h2>

            {/* Description */}
            <p className="recording-consent-description">
              You have denied to have this meeting recorded. Do you wish to change your response?
            </p>

            {/* Buttons */}
            <div className="recording-consent-buttons">
              <button onClick={handleChangeToAgree} className="recording-consent-btn-allow">
                <ShieldCheck size={20} />
                <span>Change to Agree</span>
              </button>
              <button onClick={handleDismiss} className="recording-consent-btn-reject">
                <Shield size={20} />
                <span>Dismiss</span>
              </button>
            </div>

            {/* Footer hint */}
            <p className="recording-consent-footer">
              Press Enter to Agree • Escape to Dismiss
            </p>
          </div>
        </div>
      );
    }

    // Show initial recording consent modal
    return (
      <div className="recording-consent-overlay">
        <div className="recording-consent-modal">
          {/* Icon at top */}
          <div className="recording-consent-icon-circle">
            <Video className="recording-consent-icon" size={40} />
          </div>

          {/* Heading */}
          <h2 className="recording-consent-heading">
            Recording Consent
          </h2>

          {/* Description */}
          <p className="recording-consent-description">
            The host has requested your permission to record. Recording will be saved confidentially for future reference.
          </p>

          {/* Sub text */}
          <p className="recording-consent-subtext">
            Do you agree the same ?
          </p>

          {/* Buttons */}
          <div className="recording-consent-buttons">
            <button onClick={handleYesClick} className="recording-consent-btn-allow">
              <ShieldCheck size={20} />
              <span>Agree</span>
            </button>
            <button onClick={handleNoClick} className="recording-consent-btn-reject">
              <Shield size={20} />
              <span>Deny</span>
            </button>
          </div>

          {/* Footer hint */}
          <p className="recording-consent-footer">
            Press Enter to confirm • Escape to cancel
          </p>
        </div>
      </div>
    );
  }

  // For AlreadyExist modal, keep the existing design
  return (
    <div className="dialog-overlay">
      <div className="dialog req-recrd-popup">
        <h2 className='req-record'>
          You have already Joined
        </h2>

        <p className='record-call'>
          Click the "Yes" button to exit the previously joined meeting.
        </p>

        <div className='recrd-btn-align'>
          <button onClick={handleYesClick} className='rej-btn'>
            Yes
          </button>

          <button onClick={handleNoClick} className='allw-btn'>
            No
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecordingModal;

